import React, { SyntheticEvent, useContext, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { cartContext } from 'contexts/cartContext';
import CartWithoutProduct from 'components/Checkout/Cart/CartWithoutProduct/CartWithoutProduct';
import CartWithProducts from 'components/Checkout/Cart/CartWithProducts/CartWithProducts';
import ProductSheetModal from 'components/Checkout/Cart/ProductSheetModal/ProductSheetModal';
import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import PushProductsCarousel from 'components/Common/Carousel/PushProductsCarousel/PushProductsCarousel';
import CartNotFound from 'components/Common/Errors/CartNotFound/CartNotFound';
import CustomLink from 'components/Common/CustomLink/CustomLink';
import ChevronLeft from 'components/Common/Icons/ChevronLeft';
import Footer from 'components/Common/Footer/Footer';
import Loader from 'components/Common/Loader/Loader';
import useCartMerchandising from 'hooks/checkout/useCartMerchandising';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { formatPushProducts, getSlideType } from 'services/carousel';
import { getUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { formatMerchandisingProductData } from 'services/gtm/gtmUtils';
import {
  getName,
  getRealSku,
  getCategory1,
  getCategory2,
  getCategory3,
  getDiscountPrice,
  ProductAttributes,
} from 'services/product';
import {
  MOBILE_MAX,
  SMALL_DESKTOP_MAX,
  TABLET_PORTRAIT_MAX,
  XS_MOBILE_MAX,
} from 'settings/device';
import { GTM_PAGE_CART } from 'settings/gtm/gtmPageView';
import { ProductsType, ProductType } from 'types/CartMerchandisingType';
import { CarouselPushProductType } from 'types/Carousel';

import styles from './page.module.scss';

type CarouselType = {
  products: ProductsType;
  title: string;
};

export default function Cart(): JSX.Element {
  const { cart, cartApiError } = useContext(cartContext);
  const { t } = useTranslation('checkout');
  const { pushProducts } = useCartMerchandising();
  const isEmpty = !cart?.products.length;

  useGtmPageView(GTM_PAGE_CART, `Afibel - ${t('CART_META_TITLE')}`);

  if (!cart) {
    return (
      <div className={styles.container}>
        <RenderHelmet />
        <ContinueShopping />
        {cartApiError ? <CartNotFound /> : <Loader />}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <RenderHelmet />
      <div className={styles.maxWidthWrapper}>
        <ContinueShopping />
        {isEmpty ? <CartWithoutProduct /> : <CartWithProducts cart={cart} />}
      </div>
      {pushProducts?.products.length && (
        <ErrorBoundary fallback={<></>}>
          <Carousel
            products={pushProducts.products}
            title={pushProducts.frontLabel || t('PUSH_PRODUCTS_TITLE')}
          />
        </ErrorBoundary>
      )}
      <Footer classes={{ container: styles.maxWidthWrapper }} />
    </div>
  );
}

function ContinueShopping(): JSX.Element {
  const { t } = useTranslation(['translation']);

  return (
    <CustomLink className={styles.continueLink} to={getUri('/')}>
      <ChevronLeft className={styles.chevron} />
      {t('LABEL_CONTINUE_SHOPPING')}
    </CustomLink>
  );
}

function RenderHelmet(): JSX.Element {
  const { t } = useTranslation('checkout');

  return (
    <Helmet>
      <title>
        {t('META_TITLE_TEMPLATE', {
          title: t('CART_META_TITLE'),
          ns: 'translation',
        })}
      </title>
      <meta name="description" content={t('CART_META_DESCRIPTION')} />
    </Helmet>
  );
}

function Carousel({ products, title }: CarouselType): JSX.Element {
  const [productUrlModal, setProductUrlModal] = useState('');
  const productsLenght = products.length;
  const carouselPushProducts = useMemo(
    () =>
      formatPushProducts(products, (product: ProductType) => {
        const attributes = (product.attributes as unknown) as ProductAttributes;

        return {
          category1: getCategory1(attributes),
          category2: getCategory2(attributes),
          category3: getCategory3(attributes),
          crossedOutPrice: getDiscountPrice(attributes),
          name: getName(attributes),
          sku: getRealSku(attributes),
        };
      }),
    [products]
  );

  function handleProductClick(
    product: CarouselPushProductType,
    e: SyntheticEvent
  ) {
    e.preventDefault();
    setProductUrlModal(product.url);
    pushGtmEvent({
      event: '_product_click',
      category: 'recommandation de produit',
      element: "n'oubliez pas",
      products: [formatMerchandisingProductData(product)],
    });
  }

  return (
    <>
      <PushProductsCarousel
        classes={{ wrapper: styles.carouselWrapper }}
        onProductClick={handleProductClick}
        products={carouselPushProducts}
        splideOptions={{
          perPage: 6,
          rewind: true,
          breakpoints: {
            [XS_MOBILE_MAX]: {
              padding: '20%',
              perPage: 1,
              type: getSlideType(1, productsLenght),
            },
            [MOBILE_MAX]: {
              perPage: 3,
              type: getSlideType(3, productsLenght),
            },
            [TABLET_PORTRAIT_MAX]: {
              perPage: 4,
              type: getSlideType(4, productsLenght),
            },
            [SMALL_DESKTOP_MAX]: {
              perPage: 5,
              type: getSlideType(5, productsLenght),
            },
          },
        }}
        title={title}
      />
      {productUrlModal && (
        <ProductSheetModal
          url={productUrlModal}
          onClosed={() => setProductUrlModal('')}
        />
      )}
    </>
  );
}
